import { Button, Flex, Tooltip } from "antd"
import { type ColumnType } from "antd/es/table/interface"
import React, { type ReactNode } from "react"

import Icon from "../../../../Icon"
import { type ProjectListItem } from "../../types/types.ts"

interface MenuItemType {
  key: string
  label: string
  icon: ReactNode
  danger?: boolean
  disabled?: boolean
  // Why the action is refused. antd swallows pointer events on a disabled
  // button, so the tooltip has to hang off a wrapper rather than the button.
  tooltip?: string
  className?: string
  onClick: () => void
}

interface GetProjectsColumnActionsProps {
  t: (key: string) => string
  editAllowed?: boolean
  archiveAllowed?: boolean
  restoreAllowed?: boolean
  deleteAllowed?: boolean
  onSelectRow: (record: ProjectListItem, action: string) => Promise<void> | void
}

export const getProjectsColumnActions = ({
  t,
  editAllowed,
  archiveAllowed,
  restoreAllowed,
  deleteAllowed,
  onSelectRow
}: GetProjectsColumnActionsProps): ColumnType<ProjectListItem> => {
  const allowedActions = [editAllowed, archiveAllowed, restoreAllowed, deleteAllowed]

  const getItems = (record: ProjectListItem) => {
    const menuItems: MenuItemType[] = []

    // Delete is offered on the Archive only — see `columns.tsx`, which passes
    // `deleteAllowed` just for that view.
    if (editAllowed) {
      menuItems.push({
        key: "edit",
        label: t("projectActions.edit"),
        className: "gap-1",
        icon: <Icon type={"Edit"} />,
        onClick: () => onSelectRow(record, "edit")
      })
    }

    // Keyed on the flag, not the status: EAD-642 retired status 7, so an
    // archived project still reads Completed or Cancelled.
    if (archiveAllowed && !record.is_archived) {
      menuItems.push({
        key: "archive",
        label: t("projectArchive.action"),
        className: "gap-1",
        danger: true,
        icon: <Icon type={"Archive"} />,
        onClick: () => onSelectRow(record, "archive")
      })
    }

    if (restoreAllowed) {
      menuItems.push({
        key: "restore",
        label: t("projectArchive.restore"),
        className: "gap-1",
        icon: <Icon type={"Restore"} />,
        onClick: () => onSelectRow(record, "restore")
      })
    }

    if (deleteAllowed) {
      const blockedReason = record.delete_blocked_reason
      // `can_delete` and the reason are two independent serializer fields, so
      // neither implies the other: a row blocked without a reason string would
      // otherwise render an enabled Delete that 400s on confirm.
      const blocked = record.can_delete === false || !!blockedReason
      menuItems.push({
        key: "delete",
        label: t("projectDelete.delete"),
        className: "gap-1",
        danger: true,
        icon: <Icon type={"Delete"} />,
        disabled: blocked,
        tooltip: blocked ? blockedReason || t("projectDelete.blocked") : undefined,
        onClick: () => onSelectRow(record, "delete")
      })
    }

    return menuItems
  }

  return {
    title: "Actions",
    hidden: !allowedActions.some(Boolean),
    fixed: "right",
    width: 100,
    className: "text-center",
    render: (_, record) => {
      const items = getItems(record)

      if (!items.length) return null

      // Icon-only, label on the tooltip: two labelled buttons overflow the
      // column, and a fixed column clips rather than grows.
      return (
        <Flex gap={2} justify={"center"} align={"center"}>
          {items.map(({ key, label, tooltip, ...item }) => {
            const button = (
              <Button
                type={"text"}
                size={"small"}
                aria-label={label}
                {...item}
              />
            )

            return (
              <Tooltip key={key} title={tooltip || label}>
                {tooltip ? <span>{button}</span> : button}
              </Tooltip>
            )
          })}
        </Flex>
      )
    }
  }
}
